import {
  Avatar,
  Banner,
  DataTable,
  FlexBox,
  Tag,
  Text,
  Timestamp,
} from '@dixa/lib-shared-habla';

import { BanOnUser } from 'apis/bans';
import { searchApi } from 'apis/search';
import { AppLink } from 'components/appLink';
import { channelAttributes } from 'utils/channels';
import { getUserDisplayName } from 'utils/user';

type UserBanHistoryProps = {
  bans: readonly BanOnUser[];
  isLoading: boolean;
  isError: boolean;
};

export const UserBanHistory = ({
  bans,
  isLoading,
  isError,
}: UserBanHistoryProps): JSX.Element | null => {
  const { users } = searchApi.useReadSearchUsersQuery(
    {
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'id',
            operator: 'is_one',
            values: [
              ...bans.reduce((acc, ban) => {
                if (ban.banning_agent_id) {
                  acc.add(ban.banning_agent_id);
                }
                if (ban.unbanning_agent_id) {
                  acc.add(ban.unbanning_agent_id);
                }
                return acc;
              }, new Set<string>()),
            ].map((value) => ({ value })),
          },
        ],
      },
    },
    {
      selectFromResult: ({ data }) => ({
        users: data?.data.results.map(({ result }) => result ?? []),
      }),
    },
  );

  if (isError) {
    return (
      <Banner
        variant="fail"
        icon={null}
        description={
          <Text.Paragraph>
            An error occurred while loading bans history
          </Text.Paragraph>
        }
      />
    );
  }

  if (!bans.length) {
    if (isLoading) {
      return <Text.Paragraph>Loading bans history...</Text.Paragraph>;
    }

    return (
      <Banner
        variant="info"
        icon={null}
        description={
          <Text.Paragraph>
            There are no previous bans on any channels
          </Text.Paragraph>
        }
      />
    );
  }

  return (
    <DataTable<BanOnUser, BanOnUser>
      data={bans}
      columns={[
        {
          label: '#',
          selector: function renderCsid({ csid }: BanOnUser): JSX.Element {
            if (!csid) return <>-</>;

            return (
              <AppLink
                path="/conversation/:csid"
                params={{ path: { csid: `${csid}` } }}
              >
                #{csid}
              </AppLink>
            );
          },
        },
        {
          label: 'Banned by',
          selector: function renderBannedBy({
            banning_agent_id,
          }: BanOnUser): JSX.Element {
            const user = users?.find(({ id }) => id === banning_agent_id);
            if (!user) return <>-</>;

            return (
              <FlexBox alignItems="center" gap="200">
                <Avatar user={user} />
                <Text.Paragraph>{getUserDisplayName(user)}</Text.Paragraph>
              </FlexBox>
            );
          },
        },
        {
          label: 'Channel',
          selector: function renderChannel({
            channel,
          }: BanOnUser): string | undefined {
            return (channel && channelAttributes[channel]?.name) || 'Unknown';
          },
        },
        {
          label: 'Status',
          selector: function renderStatus({
            unbanned_at,
          }: BanOnUser): JSX.Element {
            return (
              <Tag color={unbanned_at ? 'neutral.500' : 'green.500'}>
                {unbanned_at ? 'Inactive' : 'Active'}
              </Tag>
            );
          },
        },
        {
          label: 'Banned on',
          selector: function renderBannedAt({
            banned_at,
          }: BanOnUser): JSX.Element {
            return <Timestamp relative time={banned_at} />;
          },
        },
      ]}
      subTableDataSelector={(data) =>
        data.unbanned_at || data.unbanning_agent_id ? [data] : []
      }
      subTableColumns={[
        {
          label: 'Unbanned by',
          selector: function renderUnbannedBy({
            unbanning_agent_id,
          }: BanOnUser): JSX.Element | null {
            const user = users?.find(({ id }) => id === unbanning_agent_id);
            if (!user) return <>-</>;

            return (
              <FlexBox alignItems="center" gap="200">
                <Avatar user={user} />
                <Text.Paragraph>{getUserDisplayName(user)}</Text.Paragraph>
              </FlexBox>
            );
          },
        },
        {
          label: 'Unbanned on',
          selector: function renderUnbannedOn({
            unbanned_at,
          }: BanOnUser): JSX.Element | null {
            if (unbanned_at) {
              return <Timestamp relative time={unbanned_at} />;
            }
            return null;
          },
        },
      ]}
    />
  );
};
